import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, Search, ChevronRight, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AthleteFile from "./AthleteFile";

const inputCls = "w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";
const labelCls = "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5";

const TYPE_LABELS = { free_evaluation: "Free Eval", in_person: "In-Person", online: "Online", hybrid: "Hybrid", digital: "Online" };
const ATHLETE_TYPE_OPTIONS = [
  { value: "free_evaluation", label: "Free Evaluation" },
  { value: "in_person", label: "In-Person" },
  { value: "online", label: "Online Coaching" },
  { value: "hybrid", label: "Hybrid" },
];
const EMPTY_FORM = {
  name: "", username: "", password: "", email: "",
  athlete_type: "in_person", sport: "", position: "", age: "", school: "", grad_year: "", goals: "",
  parent_name: "", parent_email: "", parent_phone: "",
  membership_plan_name: "", membership_price: "", membership_interval: "monthly",
};

export default function AthletesTab() {
  const [athletes, setAthletes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  // filters
  const [q, setQ] = useState("");
  const [sport, setSport] = useState("");
  const [type, setType] = useState("");
  const [coach, setCoach] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([api.get("/athletes"), api.get("/admin/users?role=coach")]);
      setAthletes(a.data); setCoaches(c.data);
    } catch { toast.error("Failed to load athletes."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const nameOf = (id) => coaches.find((x) => x.id === id)?.name || coaches.find((x) => x.id === id)?.email || "—";
  const sports = useMemo(() => [...new Set(athletes.map((a) => a.sport).filter(Boolean))].sort(), [athletes]);

  const filtered = useMemo(() => athletes.filter((a) => {
    if (q && !(a.name?.toLowerCase().includes(q.toLowerCase()) || a.sport?.toLowerCase().includes(q.toLowerCase()))) return false;
    if (sport && a.sport !== sport) return false;
    if (type && (a.athlete_type || "in_person") !== type) return false;
    if (coach && !(a.coach_ids || []).includes(coach)) return false;
    if (status && (a.status || "active") !== status) return false;
    return true;
  }), [athletes, q, sport, type, coach, status]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.email) delete body.email;
      if (form.athlete_type === "free_evaluation") {
        delete body.membership_plan_name; delete body.membership_price; delete body.membership_interval;
      } else {
        body.membership_price = body.membership_price ? Number(body.membership_price) : undefined;
      }
      const { data } = await api.post("/admin/athletes/provision", body);
      toast.success(`Account created — @${data.username} can log in now.`);
      setForm(EMPTY_FORM); setShowPw(false); setShowAdd(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const del = async (e, id) => {
    e.stopPropagation();
    try { await api.delete(`/athletes/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  if (selected) return <AthleteFile athleteId={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div data-testid="athletes-tab">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl">Athletes</h1>
          <p className="text-white/40 text-sm mt-1" data-testid="athletes-count">{filtered.length} of {athletes.length}</p>
        </div>
        <button data-testid="admin-add-athlete" onClick={() => setShowAdd((v) => !v)} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white/85 transition-colors">
          {showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? "Cancel" : "Create Account"}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={create} className="bg-[#121212] border border-white/10 p-6 mb-8 space-y-6" data-testid="admin-athlete-form">
          <div>
            <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/50 mb-3">Athlete Type</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="aa-type-group">
              {ATHLETE_TYPE_OPTIONS.map((o) => (
                <button type="button" key={o.value} data-testid={`aa-type-${o.value}`}
                  onClick={() => setForm({ ...form, athlete_type: o.value })}
                  className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors ${form.athlete_type === o.value ? "bg-white text-black border-white" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/50 mb-3">Login Credentials</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><label className={labelCls}>Full Name *</label><input data-testid="aa-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Username *</label><input data-testid="aa-username" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s/g, "") })} placeholder="athlete handle" className={inputCls} /></div>
              <div>
                <label className={labelCls}>Password *</label>
                <div className="relative">
                  <input data-testid="aa-password" required type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 8 characters" className={`${inputCls} pr-10`} />
                  <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="aa-toggle-pw" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">{showPw ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                </div>
              </div>
              <div className="sm:col-span-3"><label className={labelCls}>Email (optional)</label><input data-testid="aa-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="used for receipts / notifications" className={inputCls} /></div>
            </div>
          </div>

          <div>
            <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/50 mb-3">Athlete Details</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><label className={labelCls}>Sport</label><input data-testid="aa-sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Position</label><input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Age</label><input data-testid="aa-age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>School</label><input value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Grad Year</label><input value={form.grad_year} onChange={(e) => setForm({ ...form, grad_year: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Goals</label><input value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} className={inputCls} /></div>
            </div>
          </div>

          <div>
            <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/50 mb-3">Parent / Guardian</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><label className={labelCls}>Parent Name</label><input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Parent Email</label><input value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Parent Phone</label><input value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} className={inputCls} /></div>
            </div>
          </div>

          {form.athlete_type !== "free_evaluation" && (
            <div data-testid="aa-membership-section">
              <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/50 mb-3">Membership (optional)</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className={labelCls}>Plan Name</label><input data-testid="aa-plan-name" value={form.membership_plan_name} onChange={(e) => setForm({ ...form, membership_plan_name: e.target.value })} placeholder="e.g. Foundation" className={inputCls} /></div>
                <div><label className={labelCls}>Price ($)</label><input data-testid="aa-plan-price" type="number" min="0" step="1" value={form.membership_price} onChange={(e) => setForm({ ...form, membership_price: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>Interval</label>
                  <select data-testid="aa-plan-interval" value={form.membership_interval} onChange={(e) => setForm({ ...form, membership_interval: e.target.value })} className={inputCls}>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                    <option value="one-time">One-time</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="pt-1"><button data-testid="aa-submit" disabled={saving} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Account</button></div>
        </form>
      )}

      {/* Filters */}
      <div className="grid sm:grid-cols-5 gap-3 mb-6">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input data-testid="af-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / sport…" className={`${inputCls} pl-9`} />
        </div>
        <select data-testid="af-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
          <option value="">All statuses</option>
          <option value="invited">Invited</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select data-testid="af-filter-sport" value={sport} onChange={(e) => setSport(e.target.value)} className={inputCls}>
          <option value="">All sports</option>
          {sports.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="af-filter-type" value={type} onChange={(e) => setType(e.target.value)} className={inputCls}>
          <option value="">All types</option>
          <option value="free_evaluation">Free Evaluation</option>
          <option value="in_person">In-Person</option>
          <option value="online">Online</option>
          <option value="hybrid">Hybrid</option>
          <option value="digital">Digital (legacy)</option>
        </select>
        <select data-testid="af-filter-coach" value={coach} onChange={(e) => setCoach(e.target.value)} className={inputCls}>
          <option value="">All coaches</option>
          {coaches.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="admin-no-athletes">{athletes.length === 0 ? "No athletes yet." : "No athletes match your filters."}</div>
      ) : (
        <div className="border border-white/10 bg-[#121212] overflow-x-auto" data-testid="admin-athletes-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/40">
                {["Name", "Sport / Position", "Status", "Type", "Coach", "Grad", "actions"].map((h) => <th key={h} className="text-left font-mono-accent text-[10px] tracking-[0.2em] uppercase px-4 py-3">{h === "actions" ? "" : h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} data-testid={`admin-athlete-${a.id}`} onClick={() => setSelected(a.id)} className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors">
                  <td className="px-4 py-3 font-display font-bold uppercase tracking-tight text-base" data-testid={`athlete-name-${a.id}`}>{a.name}</td>
                  <td className="px-4 py-3 text-white/60">{[a.sport, a.position].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-4 py-3"><span data-testid={`athlete-status-${a.id}`} className={`font-mono-accent text-[10px] tracking-[0.15em] uppercase border px-2 py-0.5 ${(a.status || "active") === "active" ? "bg-white text-black border-white" : (a.status === "invited" || a.status === "pending") ? "text-white border-white/50" : "text-white/40 border-white/20"}`}>{a.status || "active"}</span></td>
                  <td className="px-4 py-3"><span className="font-mono-accent text-[10px] tracking-[0.15em] uppercase border border-white/20 px-2 py-0.5 text-white/60">{TYPE_LABELS[a.athlete_type] || (a.athlete_type || "in_person").replace("_", "-")}</span></td>
                  <td className="px-4 py-3 text-white/60">{(a.coach_ids || []).length ? (a.coach_ids || []).map(nameOf).join(", ") : "—"}</td>
                  <td className="px-4 py-3 text-white/60">{a.grad_year || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`admin-athlete-view-${a.id}`} onClick={(e) => { e.stopPropagation(); setSelected(a.id); }} className="inline-flex items-center gap-1 text-white/50 hover:text-white text-xs uppercase tracking-widest font-bold transition-colors">File <ChevronRight size={14} /></button>
                    <button data-testid={`admin-athlete-delete-${a.id}`} onClick={(e) => del(e, a.id)} className="ml-4 text-white/25 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
